import { useMutation } from "@tanstack/react-query"
import { FileText } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import {
  ESTADO_UTILIDADES,
  sePuedeAnularUtilidades,
  type Utilidades,
} from "@/componentes/talento/planificacion/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha, hoyLocal } from "@/lib/fechas"

/**
 * Las utilidades de cada ejercicio en la ficha: cuánto toca y, si se programó, cuándo se
 * paga, con su documento.
 *
 * ⚠ **Programar congela el cálculo y emite el documento** —como las vacaciones—: cambiar
 * después los días del ejercicio o el salario NO mueve lo programado. Para eso se anula y
 * se vuelve a programar.
 */
export function UtilidadesDePersona({
  personaId,
  utilidades,
  puede,
  onCambio,
}: {
  personaId: number
  utilidades: Utilidades[]
  puede: boolean
  onCambio: () => void
}) {
  const [programando, setProgramando] = useState<number | null>(null)
  const [fecha, setFecha] = useState("")
  const [anulando, setAnulando] = useState<number | null>(null)
  const [motivo, setMotivo] = useState("")
  const hoy = hoyLocal()

  const programar = useMutation({
    mutationFn: (anio: number) =>
      api.post(`/api/v1/personas/${personaId}/utilidades`, { anio, fecha_pago: fecha }),
    onSuccess: () => {
      setProgramando(null)
      setFecha("")
      onCambio()
    },
  })
  const anular = useMutation({
    mutationFn: (id: number) => api.post(`/api/v1/utilidades-programadas/${id}/anular`, { motivo }),
    onSuccess: () => {
      setAnulando(null)
      setMotivo("")
      onCambio()
    },
  })
  const error = programar.error ?? anular.error

  return (
    <div className="space-y-2 text-sm">
      <h4 className="font-medium">Utilidades</h4>
      {utilidades.map((u) => (
        <div key={u.anio} className="superficie space-y-1 p-3">
          <div className="flex flex-wrap items-center gap-2 tabular-nums">
            <strong>Ejercicio {u.anio}</strong>
            <Insignia tono={ESTADO_UTILIDADES[u.estado].tono}>
              {ESTADO_UTILIDADES[u.estado].texto}
            </Insignia>
            <span style={{ color: "var(--fg-muted)" }}>
              {u.dias} días ({u.meses} meses de {u.dias_ejercicio}
              {u.dias_fijados ? "" : ", mínimo legal: el ejercicio no tiene días fijados"})
              {u.monto && ` · Bs. ${monto(u.monto)}`}
            </span>
          </div>

          {u.programacion && (
            <p className="tabular-nums">
              Pago el {comoFecha(u.programacion.fecha_pago)} · Bs. {monto(u.programacion.total)}{" "}
              congelados ({u.programacion.dias} días a Bs. {monto(u.programacion.salario_diario)})
              {u.programacion.documento_th_id !== null && (
                <Link
                  className="ml-2 inline-flex items-center gap-1 underline"
                  to={`/talento-humano/documento/${u.programacion.documento_th_id}`}
                >
                  <FileText size={14} aria-hidden={true} /> Documento
                </Link>
              )}
            </p>
          )}

          {puede && u.estado === "PENDIENTE" && u.monto && (
            <div className="flex flex-wrap items-center gap-2">
              {programando === u.anio ? (
                <>
                  <input
                    type="date"
                    aria-label={`Fecha de pago de las utilidades ${u.anio}`}
                    className="campo w-auto tabular-nums"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                  />
                  <Boton tamano="sm" onClick={() => setProgramando(null)}>
                    Cancelar
                  </Boton>
                  <Boton
                    tamano="sm"
                    variante="primario"
                    disabled={fecha === "" || programar.isPending}
                    onClick={() => programar.mutate(u.anio)}
                  >
                    Programar y emitir el cálculo
                  </Boton>
                </>
              ) : (
                <Boton tamano="sm" onClick={() => setProgramando(u.anio)}>
                  Programar pago
                </Boton>
              )}
            </div>
          )}

          {puede &&
            u.programacion &&
            sePuedeAnularUtilidades(u, hoy) &&
            (anulando === u.programacion.id ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  aria-label="Motivo de la anulación"
                  placeholder="Motivo (mínimo 10 caracteres)"
                  className="campo min-w-0 flex-1"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                />
                <Boton tamano="sm" onClick={() => setAnulando(null)}>
                  No
                </Boton>
                <Boton
                  tamano="sm"
                  variante="primario"
                  disabled={motivo.trim().length < 10 || anular.isPending}
                  onClick={() => u.programacion && anular.mutate(u.programacion.id)}
                >
                  Anular
                </Boton>
              </div>
            ) : (
              <Boton tamano="sm" onClick={() => setAnulando(u.programacion?.id ?? null)}>
                Anular programación
              </Boton>
            ))}
        </div>
      ))}
      {error && (
        <p role="alert" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo completar"}
        </p>
      )}
    </div>
  )
}
